"use client";

import React from "react";
import { Mail, MapPin, Clock, ShieldAlert, FileCheck2, ExternalLink } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu, type MenuItem } from "@/integrations/wordpress/WordPressMenuProvider";
function RecursiveFooterNavList({
  items,
  depth = 0
}: {
  items: MenuItem[];
  depth?: number;
}) {
  if (!items || items.length === 0) return null;
  return <ul className={depth === 0 ? "space-y-2.5" : "pl-3 mt-1.5 space-y-1.5 border-l border-border/40"}>
      {items.map((item, i) => <li key={item.id || `${item.label}-${i}`} data-index={i}>
          <Link to={item.href || "#"} className="text-sm text-foreground/80 hover:text-foreground transition-colors inline-flex items-center gap-1.5">
            <span>{item.label}</span>
          </Link>
          {item.children && item.children.length > 0 && <RecursiveFooterNavList items={item.children} depth={depth + 1} />}
        </li>)}
    </ul>;
}
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="space-y-3">
        <div className="h-4 w-24 bg-muted animate-pulse rounded-sm" />
        <div className="h-4 w-32 bg-muted animate-pulse rounded-sm" />
        <div className="h-4 w-28 bg-muted animate-pulse rounded-sm" />
        <div className="h-4 w-20 bg-muted animate-pulse rounded-sm" />
      </div>;
  }
  if (menuItems && menuItems.length > 0) {
    return <RecursiveFooterNavList items={menuItems} />;
  }
  return <ul className="space-y-2.5">
      <li data-index={0}>
        <Link to="/ShopPage" className="text-sm text-foreground/80 hover:text-foreground transition-colors">
          Product catalogue
        </Link>
      </li>
      <li data-index={1}>
        <Link to="/ResearchInformationPage" className="text-sm text-foreground/80 hover:text-foreground transition-colors">
          Research information
        </Link>
      </li>
      <li data-index={2}>
        <Link to="/ContactPage" className="text-sm text-foreground/80 hover:text-foreground transition-colors">
          Laboratory inquiries
        </Link>
      </li>
      <li data-index={3}>
        <Link to="/CartPage" className="text-sm text-foreground/80 hover:text-foreground transition-colors">
          Reagent procurement cart
        </Link>
      </li>
      <li data-index={4}>
        <Link to="/CheckoutPage" className="text-sm text-foreground/80 hover:text-foreground transition-colors">
          Institutional checkout
        </Link>
      </li>
      <li data-index={5}>
        <Link to="/MyAccountPage" className="text-sm text-foreground/80 hover:text-foreground transition-colors">
          Researcher account
        </Link>
      </li>
    </ul>;
}
export default function Footer() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 10
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="footer" data-nav="dark" className="dark bg-background text-foreground border-t border-border pt-16 pb-12">
      <div className="max-w-[1088px] mx-auto px-6">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-12">
          {/* Column 1: Identity & Regulatory Notice */}
          <div className="md:col-span-5 space-y-5">
            <div className="flex items-center">
              <Link to="/">
                <WvcLogo className="h-12 w-auto" />
              </Link>
            </div>

            <div className="rounded-md border border-border/70 bg-card/40 p-4 space-y-2">
              <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-muted-foreground font-semibold">
                <ShieldAlert className="size-4 text-primary shrink-0" />
                <span>Regulatory Notice — RUO Standard</span>
              </div>
              <p className="text-xs leading-relaxed text-foreground/85">
                All peptide reagents synthesized and catalogued by ResearchPeptide
                are strictly manufactured for in-vitro laboratory research,
                chemical profiling, and analytical standard reference. Products
                are not cleared for human ingestion, therapeutic diagnostics, or
                veterinary administration.
              </p>
            </div>

            <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground">
              <FileCheck2 className="size-3.5 text-primary shrink-0" />
              <span>Analytical HPLC &amp; Mass Spectrometry certified</span>
            </div>
          </div>

          {/* Column 2: Navigation Hierarchy */}
          <div className="md:col-span-3 space-y-4">
            <h3 className="text-xs font-bold font-mono tracking-wider uppercase text-foreground/90">
              Procurement &amp; Documentation
            </h3>
            <MenuProvider menu_id="23" className="footer-menu">
              <FooterMenuConsumer />
            </MenuProvider>
          </div>

          {/* Column 3: Technical Inquiries & Turnaround */}
          <div className="md:col-span-4 space-y-4">
            <h3 className="text-xs font-bold font-mono tracking-wider uppercase text-foreground/90">
              Scientific Support Desk
            </h3>

            <div className="space-y-3.5 text-sm">
              <div className="flex items-start gap-3">
                <Mail className="size-4 text-primary mt-0.5 shrink-0" />
                <div>
                  <div className="text-xs text-muted-foreground uppercase font-mono">
                    Direct Laboratory Inquiries
                  </div>
                  <a href="mailto:support@researchpeptide.in" className="text-foreground hover:text-primary transition-colors font-mono">
                    support@researchpeptide.in
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <MapPin className="size-4 text-primary mt-0.5 shrink-0" />
                <div>
                  <div className="text-xs text-muted-foreground uppercase font-mono">
                    Regional Distribution
                  </div>
                  <p className="text-foreground/90">
                    India (Cold-chain institutional logistics)
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Clock className="size-4 text-primary mt-0.5 shrink-0" />
                <div>
                  <div className="text-xs text-muted-foreground uppercase font-mono">
                    Operational Windows
                  </div>
                  <p className="text-foreground/90">
                    Monday to Friday, 09:00 - 18:00 IST
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Technical dossiers reviewed within 24 business hours.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <Separator className="my-10 bg-border/60" />

        {/* Bottom Bar: Copyright, GST and Compliance */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-muted-foreground font-mono">
          <p>
            &copy; {new Date().getFullYear()} ResearchPeptide. All institutional rights
            reserved.
          </p>

          <div className="flex flex-wrap items-center gap-4 text-xs">
            <span>GST-registered laboratory supply</span>
            <span className="hidden sm:inline text-border">•</span>
            <span>Batch COA verification on file</span>
          </div>
        </div>
      </div>
    </section>;
}